import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCreditNote } from "../services/creditNoteService";
import { useStoreInfo } from "../../../hooks/useCompanyInfo";
import { downloadCreditNotePDF } from "../utils/creditNoteTemplateUtils";
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';

const CreditNoteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [error, setError] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    const fetchCreditNote = async () => {
      try {
        setLoading(true);
        const data = await getCreditNote(id);
        setCreditNote(data);
        setError(null);
      } catch (err) {
        setError('Failed to load credit note');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCreditNote();
    }
  }, [id]);

  const handleDownloadPDF = async () => {
    if (!creditNote || !storeInfo) {
      setPdfError("Store information not loaded. Please refresh the page.");
      return;
    }

    try {
      setDownloadingPDF(true);
      setPdfError(null);
      await downloadCreditNotePDF(creditNote, storeInfo, t);
    } catch (err) {
      setPdfError("Failed to download PDF. Please try again.");
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Calculate totals from line items
  const calculateTotals = () => {
    if (!creditNote || !creditNote.LineItems || creditNote.LineItems.length === 0) {
      return { subTotal: 0, vatTotal: 0, total: 0 };
    }

    let subTotal = 0;
    let vatTotal = 0;

    creditNote.LineItems.forEach(item => {
      const itemSubTotal = (item.Quantity || 0) * (item.UnitPrice || 0);
      const itemVat = itemSubTotal * ((item.VAT || 0) / 100);
      subTotal += itemSubTotal;
      vatTotal += itemVat;
    });

    return {
      subTotal: subTotal.toFixed(2),
      vatTotal: vatTotal.toFixed(2),
      total: (subTotal + vatTotal).toFixed(2)
    };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/creditnotes')}
            sx={{ mt: 2 }}
          >
            Back to List
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!creditNote) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Credit note not found</Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/creditnotes')}
            sx={{ mt: 2 }}
          >
            Back to List
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {pdfError && (
        <Alert severity="error" onClose={() => setPdfError(null)} sx={{ mb: 2 }}>
          {pdfError}
        </Alert>
      )}
      
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#c62828' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="#c62828">
                {creditNote.CreditNoteNumber || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Credit Note Details
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/creditnotes')}
            >
              Back to List
            </Button>
            <Button
              variant="contained"
              startIcon={downloadingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={downloadingPDF || !storeInfo}
              sx={{ bgcolor: '#1976d2' }}
            >
              {downloadingPDF ? 'Downloading...' : 'Download PDF'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Credit Note Info */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Credit Note Date</Typography>
            <Typography variant="body1" fontWeight="bold">
              {creditNote.CreditNoteDate ? new Date(creditNote.CreditNoteDate).toLocaleDateString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Invoice ID</Typography>
            <Typography variant="body1" fontWeight="bold">
              #{creditNote.InvoiceId || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Peppol Status</Typography>
            <Chip 
              label={creditNote.PeppolStatus || 'Draft'} 
              color={creditNote.PeppolStatus === 'Sent' ? 'success' : 'default'}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Peppol Message ID</Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>
              {creditNote.PeppolMessageId || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">Reason</Typography>
            <Typography variant="body1">
              {creditNote.Reason || 'No reason provided'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Line Items */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Line Items
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Product</TableCell>
              <TableCell align="center">Quantity</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">VAT %</TableCell>
              <TableCell align="right">Subtotal</TableCell>
              <TableCell align="right">Total (Incl. VAT)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {creditNote.LineItems && creditNote.LineItems.length > 0 ? (
              creditNote.LineItems.map((item, index) => {
                const itemSubTotal = (item.Quantity || 0) * (item.UnitPrice || 0);
                const itemVat = itemSubTotal * ((item.VAT || 0) / 100);
                const itemTotal = itemSubTotal + itemVat;
                
                return (
                  <TableRow key={item.Id || index}>
                    <TableCell>{item.Product?.Name || `Product #${item.ProductId}` || 'N/A'}</TableCell>
                    <TableCell align="center">{item.Quantity || 0}</TableCell>
                    <TableCell align="right">€{(item.UnitPrice || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{(item.VAT || 0).toFixed(2)}%</TableCell>
                    <TableCell align="right">€{itemSubTotal.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>€{itemTotal.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No line items found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Totals */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}></Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal (Excl. VAT):</Typography>
              <Typography variant="body1" fontWeight="bold">-€{totals.subTotal}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Total VAT:</Typography>
              <Typography variant="body1" fontWeight="bold">-€{totals.vatTotal}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold">Total Credit:</Typography>
              <Typography variant="h6" fontWeight="bold" color="#c62828">-€{totals.total}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default CreditNoteDetailPage;